/**
 * Sonidos de aviso (2026-08-19): una CAMPANA cuando la campana de
 * avisos trae algo nuevo al entrar/recargar, y una ALARMA cuando enfermería
 * marca el visto de un paciente.
 *
 * Sintetizados con WebAudio — sin ficheros de audio que empaquetar. Detalle
 * importante: los navegadores BLOQUEAN el sonido hasta que el usuario toca
 * algo. Si el sonido llega antes del primer gesto (justo el caso de "al
 * recargar la sesión"), se deja en cola y suena en el primer clic o tecla.
 */

let ctx = null;
let pendiente = null;      // el último sonido que el navegador bloqueó
let escuchando = false;

function contexto() {
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  return ctx;
}

function alPrimerGesto() {
  if (escuchando) return;
  escuchando = true;
  const tocar = () => {
    document.removeEventListener('pointerdown', tocar);
    document.removeEventListener('keydown', tocar);
    escuchando = false;
    const c = contexto();
    if (c && c.state === 'suspended') c.resume();
    const p = pendiente;
    pendiente = null;
    if (p) setTimeout(p, 50);
  };
  document.addEventListener('pointerdown', tocar, { once: true });
  document.addEventListener('keydown', tocar, { once: true });
}

/** Toca una lista de notas [{f, t0, dur, vol, tipo}] sobre el contexto. */
function tocarNotas(notas) {
  const c = contexto();
  if (!c) return;
  if (c.state === 'suspended') {
    // Bloqueado hasta el primer gesto: se encola y se avisa al primer clic.
    pendiente = () => tocarNotas(notas);
    alPrimerGesto();
    return;
  }
  const ahora = c.currentTime;
  for (const n of notas) {
    const osc = c.createOscillator();
    const gan = c.createGain();
    osc.type = n.tipo || 'sine';
    osc.frequency.value = n.f;
    gan.gain.setValueAtTime(0, ahora + n.t0);
    gan.gain.linearRampToValueAtTime(n.vol ?? 0.18, ahora + n.t0 + 0.01);
    gan.gain.exponentialRampToValueAtTime(0.0001, ahora + n.t0 + n.dur);
    osc.connect(gan).connect(c.destination);
    osc.start(ahora + n.t0);
    osc.stop(ahora + n.t0 + n.dur + 0.05);
  }
}

/** Campana de avisos: dos golpes de campana (ding… dong). */
export function sonarCampana() {
  tocarNotas([
    { f: 880, t0: 0, dur: 0.9, vol: 0.15 },
    { f: 1320, t0: 0, dur: 0.5, vol: 0.06 },     // armónico del primer golpe
    { f: 660, t0: 0.45, dur: 1.1, vol: 0.15 },
    { f: 990, t0: 0.45, dur: 0.6, vol: 0.05 },
  ]);
}

/** Alarma del visto de enfermería: tres toques ascendentes, que se oigan. */
export function sonarAlarma() {
  tocarNotas([
    { f: 740, t0: 0, dur: 0.18, vol: 0.2, tipo: 'triangle' },
    { f: 880, t0: 0.22, dur: 0.18, vol: 0.2, tipo: 'triangle' },
    { f: 1175, t0: 0.44, dur: 0.35, vol: 0.22, tipo: 'triangle' },
  ]);
}

/** Pitido de "en recuperación" (quirófano): dos toques cortos y secos. */
export function sonarPitido() {
  tocarNotas([
    { f: 1050, t0: 0, dur: 0.12, vol: 0.2, tipo: 'square' },
    { f: 1050, t0: 0.2, dur: 0.12, vol: 0.2, tipo: 'square' },
  ]);
}

/** Soniquete de "listo para cambiarse": LARGO, claro y alto (petición
 *  literal de dirección, 2026-08-19) — un motivo de campanilla ascendente que
 *  se repite dos veces, ~3 segundos. Es la llamada a enfermería/anestesia. */
export function sonarSoniquete() {
  const motivo = (t0) => ([
    { f: 660, t0, dur: 0.3, vol: 0.3 },
    { f: 880, t0: t0 + 0.3, dur: 0.3, vol: 0.3 },
    { f: 1100, t0: t0 + 0.6, dur: 0.45, vol: 0.32 },
    { f: 880, t0: t0 + 1.05, dur: 0.5, vol: 0.28 },
  ]);
  tocarNotas([...motivo(0), ...motivo(1.6)]);
}

/** Llegada a sala de espera (para enfermería): dos tonos suaves, ding-dong
 *  descendente — avisa sin sobresaltar, que suena muchas veces al día. */
export function sonarLlegada() {
  tocarNotas([
    { f: 988, t0: 0, dur: 0.35, vol: 0.18 },
    { f: 784, t0: 0.3, dur: 0.5, vol: 0.18 },
  ]);
}
